import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  Price,
  SellerBadge,
  UnavailableSection,
} from '@beautyburst/ui';
import { FallbackMediaResolver, R2MediaResolver } from '@beautyburst/catalog';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type { CustomerApiClient } from './api-client.ts';
import { App, SignInPrompt, type CustomerActor } from './App.tsx';
import { CustomerApiError, createCustomerHttpClient } from './http-client.ts';
import type { RouteContract, RouteMatch } from './routes.ts';
import { CategoriesScreen } from './screens/categories.tsx';
import { CartScreen } from './screens/cart.tsx';
import { FavoritesScreen } from './screens/favorites.tsx';
import { ListsScreen } from './screens/lists.tsx';
import { AddressesScreen } from './screens/addresses.tsx';
import { AccountScreen } from './screens/account.tsx';
import { ProductDetailScreen, type ProductDetailData } from './screens/product-detail.tsx';
import { SearchScreen } from './screens/search.tsx';
import { SignInScreen } from './screens/sign-in.tsx';
import { SignUpScreen } from './screens/sign-up.tsx';
import { ForgotPasswordScreen } from './screens/forgot-password.tsx';
import { AcceptInvitationScreen } from './screens/accept-invitation.tsx';
import { PartnerScreen } from './screens/partner.tsx';
import { BeautyBurstPlusScreen } from './screens/beauty-burst-plus.tsx';
import { StorefrontScreen, type StorefrontData } from './screens/storefront.tsx';
import { StoresScreen } from './screens/stores.tsx';
import { AfterHoursScreen } from './screens/after-hours.tsx';
import { NotificationsScreen } from './screens/notifications.tsx';
import { OrderDetailScreen, OrdersScreen } from './screens/orders.tsx';
import { OrderHelpScreen } from './screens/order-help.tsx';
import type { CustomerBootstrap } from './api-client.ts';
import { customerHref } from './base-path.ts';
import {
  readSelectedMarketId,
  selectedAvailableMarketId,
  writeSelectedMarketId,
} from './market-session.ts';
import {
  CartAddFeedback,
  CartAddFeedbackProvider,
  useCartAddFeedback,
} from './cart-add-feedback.tsx';
import { requestWithRetry } from './request-retry.ts';

type BootstrapPayload = CustomerBootstrap & { readonly actor: CustomerActor };

type RemoteState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly value: T }
  | { readonly status: 'failed'; readonly error: unknown };

function messageFor(): string {
  return 'We could not load this information right now. Please try again.';
}

function isUnauthenticated(error: unknown): error is CustomerApiError {
  return error instanceof CustomerApiError && error.code === 'UNAUTHENTICATED';
}

function safeCustomerReturnTo(search: string): string {
  const requested = new URLSearchParams(search).get('returnTo');
  return requested !== null && requested.startsWith('/') && !requested.startsWith('//')
    ? requested
    : '/';
}

function logDiagnostic(error: unknown): void {
  if (error instanceof CustomerApiError) return;
  console.error('Customer API request failed.');
}

function useRouteData(
  client: CustomerApiClient,
  route: RouteMatch<RouteContract>,
  selectedMarketId: string | null,
): readonly [RemoteState<unknown>, () => void] {
  const [state, setState] = useState<RemoteState<unknown>>({ status: 'loading' });
  const [reloadVersion, setReloadVersion] = useState(0);
  const queryString = window.location.search;
  const query = new URLSearchParams(queryString);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    const load = () => {
      switch (route.route.screen) {
        case 'Categories':
          return selectedMarketId === null
            ? Promise.resolve([])
            : client.discovery.categories(selectedMarketId);
        case 'Category':
          return selectedMarketId === null
            ? Promise.resolve(null)
            : client.discovery.category(route.params.categorySlug!, selectedMarketId);
        case 'Search results': {
          const term = query.get('q') ?? '';
          return term === ''
            ? Promise.resolve({ term, result: { cards: [], total: 0 } })
            : selectedMarketId === null
              ? Promise.resolve({ term, result: { cards: [], total: 0 } })
              : client.search
                  .search({ term }, selectedMarketId)
                  .then((result) => ({ term, result }));
        }
        case 'Store front':
          return selectedMarketId === null
            ? Promise.resolve(null)
            : client.stores.detail(route.params.storeSlug!, selectedMarketId);
        case 'Product detail':
          return selectedMarketId === null
            ? Promise.resolve(null)
            : client.products.detail(route.params.productSlug!, selectedMarketId);
        case 'Cart':
          return client.cart.read();
        case 'Account':
          return client.account.hub();
        case 'Favorites':
          return client.favorites.page();
        case 'Shopping lists':
          return client.lists.list();
        case 'Shopping list detail':
          return Promise.all([client.lists.list(), client.lists.read(route.params.listId!)]).then(
            ([lists, selectedList]) => ({ lists, selectedList }),
          );
        case 'Addresses':
          return client.addresses.list();
        case 'Stores':
          return selectedMarketId === null
            ? Promise.resolve([])
            : client.stores.nearby(selectedMarketId);
        case 'Beauty After Hours':
          return selectedMarketId === null
            ? Promise.resolve([])
            : client.stores.afterHours(selectedMarketId);
        case 'Order history':
          return client.orders.list();
        case 'Order detail':
          return client.orders.detail(route.params.orderId!).then(async (order) => {
            const fulfilmentIds = order.subOrders.flatMap((subOrder) =>
              typeof subOrder.fulfilment_id === 'string' && subOrder.fulfilment_id.length > 0
                ? [subOrder.fulfilment_id]
                : [],
            );
            const tracking = await Promise.all(
              fulfilmentIds.map((fulfilmentId) => client.orders.tracking(fulfilmentId)),
            );
            return { order, tracking };
          });
        case 'Order help':
          return Promise.resolve(null);
        case 'Notification preferences':
          return client.notifications.get();
        case 'Beauty Burst Plus':
          return client.membership.plans();
        default:
          return Promise.resolve(null);
      }
    };
    void requestWithRetry(load).then(
      (value) => {
        if (active) setState({ status: 'loaded', value });
      },
      (error: unknown) => {
        if (active) setState({ status: 'failed', error });
      },
    );
    return () => {
      active = false;
    };
  }, [client, queryString, reloadVersion, route, selectedMarketId]);

  return [state, () => setReloadVersion((version) => version + 1)];
}

export function CategoryScreen({
  value,
  client,
}: {
  value: unknown;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const data = value as
    | {
        readonly category?: { readonly name?: unknown };
        readonly products?: readonly {
          readonly listingId: string;
          readonly productSlug: string;
          readonly productName: string;
          readonly variantName: string;
          readonly priceCents: number;
          readonly salePriceCents: number | null;
          readonly saleActive: boolean;
          readonly sellerDisplayName: string;
          readonly ownershipType: 'RETAILER' | 'BEAUTY_BURST_DIRECT';
          readonly available: number | null;
        }[];
      }
    | null
    | undefined;
  const categoryName = data?.category?.name;
  const products = data?.products;
  if (typeof categoryName !== 'string' || categoryName.trim() === '' || !Array.isArray(products))
    return (
      <UnavailableSection
        title="Category unavailable"
        reason="We could not load this category right now. Please return to categories and try again."
      />
    );
  return (
    <section aria-labelledby="category-heading">
      <h1 id="category-heading">{categoryName}</h1>
      {products.length === 0 ? (
        <section aria-labelledby="category-status-title">
          <h2
            id="category-status-title"
            style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
          >
            Category availability
          </h2>
          <EmptyState
            title="No products in this category yet"
            description="Browse another category to find beauty products available in your market."
            action={<a href={customerHref('/categories')}>Browse categories</a>}
          />
        </section>
      ) : (
        <ul
          aria-label={`${categoryName} products`}
          style={{ display: 'grid', gap: space[3], padding: 0 }}
        >
          {products.map((product) => (
            <CategoryProductRow key={product.listingId} product={product} client={client} />
          ))}
        </ul>
      )}
    </section>
  );
}

function CategoryProductRow({
  product,
  client,
}: {
  product: {
    readonly listingId: string;
    readonly productSlug: string;
    readonly productName: string;
    readonly variantName: string;
    readonly priceCents: number;
    readonly salePriceCents: number | null;
    readonly saleActive: boolean;
    readonly sellerDisplayName: string;
    readonly ownershipType: 'RETAILER' | 'BEAUTY_BURST_DIRECT';
    readonly available: number | null;
  };
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const { addToBag, dismiss, feedback } = useCartAddFeedback(client);
  const canAddToBag = product.available !== null && product.available > 0;
  return (
    <li style={{ listStyle: 'none' }}>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary, marginTop: 0 }}>
          <a href={customerHref(`/products/${product.productSlug}`)}>{product.productName}</a>
        </h2>
        <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
          {product.variantName}
        </p>
        <Price
          priceCents={product.priceCents}
          saleActive={product.saleActive}
          salePriceCents={product.salePriceCents}
        />
        <SellerBadge
          retailerName={
            product.ownershipType === 'BEAUTY_BURST_DIRECT' ? null : product.sellerDisplayName
          }
        />
        <Button
          variant="primary"
          size="sm"
          disabled={!canAddToBag}
          onClick={() => void addToBag({ listingId: product.listingId, quantity: 1 })}
        >
          Add to Bag
        </Button>
        <CartAddFeedback feedback={feedback} onDismiss={dismiss} />
      </Card>
    </li>
  );
}

export function CustomerRouteScreen({
  client,
  route,
  onSessionChanged,
  selectedMarketId,
  signedIn: isSignedIn = false,
  onNavigate,
  mediaUrlFor,
}: {
  readonly client: CustomerApiClient;
  readonly route: RouteMatch<RouteContract>;
  readonly onSessionChanged: () => void;
  readonly selectedMarketId: string | null;
  readonly signedIn?: boolean;
  readonly onNavigate?: (path: string) => void;
  readonly mediaUrlFor?: (mediaKey: string) => string | null;
}) {
  const [data, retry] = useRouteData(client, route, selectedMarketId);
  if (data.status === 'loading') return <LoadingState label={`Loading ${route.route.screen}`} />;
  if (data.status === 'failed') {
    logDiagnostic(data.error);
    if (isUnauthenticated(data.error)) {
      const description =
        route.route.screen === 'Cart'
          ? 'Sign in to view your cart and continue shopping.'
          : 'Your session has ended. Sign in again to continue.';
      return (
        <SignInPrompt
          description={description}
          returnTo={`${window.location.pathname}${window.location.search}`}
        />
      );
    }
    return (
      <section>
        <UnavailableSection title={`${route.route.screen} unavailable`} reason={messageFor()} />
        <Button type="button" onClick={retry}>
          Retry
        </Button>
      </section>
    );
  }

  switch (route.route.screen) {
    case 'Categories':
      return <CategoriesScreen categories={data.value as never} mediaUrlFor={mediaUrlFor} />;
    case 'Category':
      return <CategoryScreen value={data.value} client={client} />;
    case 'Search results': {
      const value = data.value as { readonly term: string; readonly result: unknown };
      return (
        <SearchScreen
          query={value.term}
          result={value.result as never}
          client={client}
          onSearch={onNavigate}
          mediaUrlFor={mediaUrlFor}
        />
      );
    }
    case 'Store front':
      return (
        <StorefrontScreen
          store={data.value as StorefrontData}
          client={client}
          onSearch={onNavigate}
          mediaUrlFor={mediaUrlFor}
        />
      );
    case 'Product detail':
      return (
        <ProductDetailScreen
          detail={data.value as ProductDetailData}
          client={client}
          mediaUrlFor={mediaUrlFor}
        />
      );
    case 'Cart':
      return <CartScreen cart={data.value as never} client={client} />;
    case 'Account':
      return (
        <AccountScreen
          hub={data.value as never}
          client={client}
          onSignOut={async () => {
            await client.auth.signOut();
            onSessionChanged();
          }}
        />
      );
    case 'Favorites':
      return <FavoritesScreen page={data.value as never} client={client} />;
    case 'Shopping lists': {
      return <ListsScreen lists={data.value as never} client={client} />;
    }
    case 'Shopping list detail': {
      const value = data.value as { readonly lists: unknown; readonly selectedList: unknown };
      return (
        <ListsScreen
          lists={value.lists as never}
          selectedList={value.selectedList as never}
          client={client}
        />
      );
    }
    case 'Addresses':
      return <AddressesScreen addresses={data.value as never} client={client} />;
    case 'Stores':
      return <StoresScreen stores={data.value as never} mediaUrlFor={mediaUrlFor} />;
    case 'Beauty After Hours':
      return <AfterHoursScreen stores={data.value as never} />;
    case 'Order history':
      return <OrdersScreen orders={data.value as never} />;
    case 'Order detail': {
      const value = data.value as { readonly order: unknown; readonly tracking: unknown };
      return <OrderDetailScreen order={value.order as never} tracking={value.tracking as never} />;
    }
    case 'Order help':
      return <OrderHelpScreen />;
    case 'Notification preferences':
      return <NotificationsScreen preferences={data.value as never} client={client} />;
    case 'Sign in': {
      const returnTo = safeCustomerReturnTo(window.location.search);
      return (
        <SignInScreen
          client={client}
          returnTo={returnTo}
          onSignedIn={(destination) => {
            onNavigate?.(destination);
            onSessionChanged();
          }}
        />
      );
    }
    case 'Sign up':
      return <SignUpScreen client={client} onSignedUp={onSessionChanged} />;
    case 'Forgot password':
      return <ForgotPasswordScreen client={client} />;
    case 'Accept invitation':
      return <AcceptInvitationScreen />;
    case 'Retailer partnership':
      return <PartnerScreen client={client} />;
    case 'Beauty Burst Plus':
      return (
        <BeautyBurstPlusScreen
          client={client}
          marketId={selectedMarketId}
          plans={data.value as Awaited<ReturnType<CustomerApiClient['membership']['plans']>>}
          signedIn={isSignedIn}
        />
      );
    default:
      return (
        <UnavailableSection title="Not available yet" reason="This screen is not available." />
      );
  }
}

function BrowserAppContent({
  client,
  bootstrap,
  onSessionChanged,
  onMarketChange,
  marketChangePending,
  marketChangeError,
  onRetryMarketChange,
}: {
  readonly client: CustomerApiClient;
  readonly bootstrap: BootstrapPayload;
  readonly onSessionChanged: () => void;
  readonly onMarketChange: (marketId: string) => void;
  readonly marketChangePending: boolean;
  readonly marketChangeError: boolean;
  readonly onRetryMarketChange: () => void;
}) {
  const mediaResolver = useMemo(
    () =>
      bootstrap.mediaPublicBaseUrl === null
        ? new FallbackMediaResolver()
        : new R2MediaResolver(bootstrap.mediaPublicBaseUrl),
    [bootstrap.mediaPublicBaseUrl],
  );
  const mediaUrlFor = (mediaKey: string) => mediaResolver.resolve(mediaKey);
  const [location, setLocation] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
  }));
  const navigate = (destination: string) => {
    const url = new URL(destination, window.location.origin);
    window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
    setLocation({ pathname: url.pathname, search: url.search });
  };

  useEffect(() => {
    const onPopState = () =>
      setLocation({ pathname: window.location.pathname, search: window.location.search });
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey
      )
        return;
      const target = event.target;
      const anchor = target instanceof Element ? target.closest('a[href]') : null;
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.target !== '' ||
        anchor.hasAttribute('download')
      )
        return;
      const destination = new URL(anchor.href, window.location.origin);
      if (destination.origin !== window.location.origin) return;
      event.preventDefault();
      navigate(`${destination.pathname}${destination.search}${destination.hash}`);
    };
    window.addEventListener('popstate', onPopState);
    document.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <CartAddFeedbackProvider
      scopeKey={`${location.pathname}${location.search}:${bootstrap.selectedMarketId ?? 'no-market'}`}
    >
      <App
        path={location.pathname}
        client={client}
        actor={bootstrap.actor}
        selectedMarketId={bootstrap.selectedMarketId}
        markets={bootstrap.markets}
        onMarketChange={onMarketChange}
        marketChangePending={marketChangePending}
        marketChangeError={marketChangeError}
        onRetryMarketChange={onRetryMarketChange}
        flags={
          new Set(
            Object.entries(bootstrap.flags).flatMap(([key, enabled]) => (enabled ? [key] : [])),
          )
        }
        renderScreen={(route) => (
          <CustomerRouteScreen
            client={client}
            route={route}
            onSessionChanged={onSessionChanged}
            selectedMarketId={bootstrap.selectedMarketId}
            signedIn={bootstrap.actor.signedIn}
            onNavigate={navigate}
            mediaUrlFor={mediaUrlFor}
            key={`${route.route.path}:${bootstrap.selectedMarketId ?? 'no-market'}`}
          />
        )}
      />
    </CartAddFeedbackProvider>
  );
}

interface BootstrapRequest {
  readonly marketId: string | undefined;
  readonly kind: 'initial' | 'market' | 'session';
  readonly version: number;
}

export interface BrowserCustomerAppProps {
  /** Dependency injection keeps market-change failures deterministic in DOM tests. */
  readonly client?: CustomerApiClient;
}

export function BrowserCustomerApp({ client: suppliedClient }: BrowserCustomerAppProps = {}) {
  const createdClient = useMemo(() => createCustomerHttpClient(), []);
  const client = suppliedClient ?? createdClient;
  const [bootstrap, setBootstrap] = useState<RemoteState<BootstrapPayload>>({ status: 'loading' });
  const [request, setRequest] = useState<BootstrapRequest>(() => ({
    marketId: readSelectedMarketId(),
    kind: 'initial',
    version: 0,
  }));
  const [marketChangePending, setMarketChangePending] = useState(false);
  const [marketChangeError, setMarketChangeError] = useState<string | null>(null);
  const lastSuccessfulBootstrap = useRef<BootstrapPayload | null>(null);
  const latestRequestVersion = useRef(0);

  useEffect(() => {
    let active = true;
    void requestWithRetry(() => client.bootstrap.get(request.marketId)).then(
      (value) => {
        if (active && request.version === latestRequestVersion.current) {
          const selectedMarketId = selectedAvailableMarketId(value.markets, value.selectedMarketId);
          const nextBootstrap = { ...value, selectedMarketId };
          writeSelectedMarketId(selectedMarketId);
          lastSuccessfulBootstrap.current = nextBootstrap;
          setBootstrap({ status: 'loaded', value: nextBootstrap });
          setMarketChangePending(false);
          setMarketChangeError(null);
        }
      },
      (error: unknown) => {
        if (!active || request.version !== latestRequestVersion.current) return;
        if (request.kind === 'market' && lastSuccessfulBootstrap.current !== null) {
          setMarketChangePending(false);
          setMarketChangeError(request.marketId ?? null);
          return;
        }
        setBootstrap({ status: 'failed', error });
      },
    );
    return () => {
      active = false;
    };
  }, [client, request]);

  const refreshSession = () => {
    const version = latestRequestVersion.current + 1;
    latestRequestVersion.current = version;
    setRequest((current) => ({
      marketId: lastSuccessfulBootstrap.current?.selectedMarketId ?? current.marketId,
      kind: 'session',
      version,
    }));
  };

  const changeMarket = (marketId: string) => {
    if (!marketChangePending && marketId === lastSuccessfulBootstrap.current?.selectedMarketId)
      return;
    const version = latestRequestVersion.current + 1;
    latestRequestVersion.current = version;
    setMarketChangePending(true);
    setMarketChangeError(null);
    setRequest({ marketId, kind: 'market', version });
  };

  const retryBootstrap = () => {
    const version = latestRequestVersion.current + 1;
    latestRequestVersion.current = version;
    setBootstrap({ status: 'loading' });
    setRequest((current) => ({ ...current, version }));
  };

  const retryMarketChange = () => {
    if (marketChangeError === null) return;
    changeMarket(marketChangeError);
  };

  if (bootstrap.status === 'loading') return <LoadingState label="Loading Beauty Burst" />;
  if (bootstrap.status === 'failed')
    return (
      <section>
        <UnavailableSection
          title="Beauty Burst is unavailable"
          reason="We could not establish your customer experience. Please try again."
        />
        <Button type="button" onClick={retryBootstrap}>
          Retry Beauty Burst
        </Button>
      </section>
    );
  return (
    <BrowserAppContent
      client={client}
      bootstrap={bootstrap.value}
      onSessionChanged={refreshSession}
      onMarketChange={changeMarket}
      marketChangePending={marketChangePending}
      marketChangeError={marketChangeError !== null}
      onRetryMarketChange={retryMarketChange}
    />
  );
}
